/**
 * FrameRenderer.ts — Panel-based layout for full-viewport framed UI.
 *
 * Draws box-drawing character borders around the entire viewport,
 * divides the screen into named regions (header, content, footer),
 * and provides utilities for writing centered or panel-aligned text.
 *
 * Uses double-line box-drawing characters: ╔ ═ ╗ ║ ╚ ╝ ╠ ╣ ╦ ╩
 *
 * Adapted from RotaryKnights for Gallowcrest — frost blue/silver colors.
 */

import type { Terminal } from './Terminal';
import type { TextEffects } from './TextEffects';
import type { TerminalFrame } from './TerminalFrame';
import { PanelWriter } from './PanelWriter';
import { theme, type ColorName, RESET } from './ColorTheme';

// ─── Panel Region ──────────────────────────────────────────

export interface PanelRegion {
  /** First row of content inside this panel (1-based) */
  startRow: number;
  /** Last row of content inside this panel (1-based) */
  endRow: number;
  /** First column of content (after left border + padding) */
  startCol: number;
  /** Usable width in characters */
  width: number;
  /** Usable height in rows */
  height: number;
}

export interface FrameLayout {
  /** Full viewport dimensions */
  cols: number;
  rows: number;
  /** Header region (title bar, 1 row) */
  header: PanelRegion;
  /** Main content region */
  content: PanelRegion;
  /** Footer region (stats bar, 1 row) */
  footer: PanelRegion;
  /** Left panel (for two-column layouts, undefined for single-column) */
  leftPanel?: PanelRegion;
  /** Right panel (for two-column layouts, undefined for single-column) */
  rightPanel?: PanelRegion;
}

// ─── Frame Config ──────────────────────────────────────────

export interface FrameConfig {
  /** Title text for the header bar (left side) */
  title?: string;
  /** Right-aligned text in header (e.g., "Chapter 3  Gallowcrest Citadel") */
  headerRight?: string;
  /** Footer text (stats summary line) */
  footer?: string;
  /** Border color */
  borderColor?: ColorName;
  /** Header text color */
  headerColor?: ColorName;
  /** If true, split content into left/right panels */
  twoColumn?: boolean;
  /** Width ratio for left panel in two-column mode (0.0-1.0, default 0.45) */
  leftRatio?: number;
}

// ─── FrameRenderer ─────────────────────────────────────────

export class FrameRenderer {
  private terminal: Terminal;
  private currentLayout: FrameLayout | null = null;
  private terminalFrame: TerminalFrame | null = null;

  // Padding to prevent right-aligned header text from overlapping border
  private readonly HEADER_RIGHT_PADDING = 3;

  constructor(terminal: Terminal) {
    this.terminal = terminal;
  }

  /** Set the TerminalFrame reference so drawFrame can skip outer ASCII borders when CSS frame is active. */
  setTerminalFrame(frame: TerminalFrame): void {
    this.terminalFrame = frame;
  }

  /** Check if the CSS terminal frame is currently active and has a DOM element. */
  private isCssFrameActive(): boolean {
    if (!this.terminalFrame?.isEnabled()) return false;
    // Verify the CSS frame element actually exists in the DOM before skipping ASCII fallback
    return document.querySelector('.frost-citadel-frame') !== null;
  }

  /** Get the current frame layout (null if no frame drawn yet) */
  getLayout(): FrameLayout | null {
    return this.currentLayout;
  }

  /** Create a PanelWriter for writing frame-aware content within a panel region. */
  createWriter(panel: PanelRegion, effects: TextEffects): PanelWriter {
    return new PanelWriter(this.terminal, effects, panel);
  }

  /**
   * Draw a full-viewport frame and return the layout regions.
   * Clears the screen first.
   */
  drawFrame(config: FrameConfig = {}): FrameLayout {
    const {
      title = '',
      headerRight = '',
      footer = '',
      borderColor = 'frostBlue',
      headerColor = 'icyWhite',
      twoColumn = false,
      leftRatio = 0.45,
    } = config;

    const { cols, rows } = this.terminal.getSize();
    const bc = theme.fg(borderColor);
    const hc = theme.fg(headerColor);
    // Use brighter/bolder color for corner glyphs to improve visibility on low-contrast terminals
    const cc = `\x1b[1m${theme.fg('brightWhite')}`; // BOLD + bright white
    const cssFrame = this.isCssFrameActive();

    // Hide cursor BEFORE clear to prevent block cursor artifact at (1,1)
    this.terminal.hideCursor();
    this.terminal.clear();

    if (cssFrame) {
      // ── CSS frame active: skip outer ASCII border, draw internal structure only ──

      // Row 1: Title row (no ╔/╗ border chars — CSS frame handles outer edge)
      this.terminal.setCursor(1, 1);
      const titleText = title ? ` ${title} ` : '';
      // Clamp headerRight to prevent overflow on narrow terminals
      const maxRight = Math.floor(cols / 3);
      const clampedHeaderRight = headerRight && headerRight.length > maxRight
        ? headerRight.slice(0, maxRight - 1) + '…'
        : headerRight;
      const rightText = clampedHeaderRight ? ` ${clampedHeaderRight} ` : '';
      const rightPad = this.HEADER_RIGHT_PADDING; // Prevent text from overlapping border
      const leadChars = 3; // multiple ═ before title to prevent single-char misread as *
      const titleFill = cols - leadChars - 1 - titleText.length - rightText.length - rightPad;
      this.terminal.writeRaw(
        `${bc}${'═'.repeat(leadChars)}${hc}${titleText}${bc}${'═'.repeat(Math.max(0, titleFill))}${hc}${rightText}${bc}${'═'.repeat(1 + rightPad)}${RESET}`
      );

      // Row 2: Header separator (full width, no ╠/╣)
      this.terminal.setCursor(2, 1);
      this.terminal.writeRaw(`${bc}${'═'.repeat(cols)}${RESET}`);

      // Content area — no side border chars needed (CSS handles sides)

      // Footer — draw at rows-2 (one row up) to avoid overlap with CSS bottom bar
      const footerRow = footer ? rows - 2 : rows;
      if (footer) {
        // Route footer text to CSS bottom bar
        if (this.terminalFrame) {
          this.terminalFrame.updateBottomBar(footer);
        }
        // Also draw an in-terminal separator for visual structure
        this.terminal.setCursor(footerRow, 1);
        const footerPadded = this.padCenter(footer, cols);
        this.terminal.writeRaw(`${bc}${'─'.repeat(cols)}${RESET}`);
        // Overwrite with footer text centered
        this.terminal.setCursor(footerRow, 1);
        this.terminal.writeRaw(`${theme.fg('khaki')}${footerPadded}${RESET}`);
      }

      // Calculate content regions — no border chars consuming columns
      const contentPadding = 2; // 2 char padding on each side for breathing room
      const contentStartCol = 1 + contentPadding;
      const contentWidth = cols - (contentPadding * 2);

      const header: PanelRegion = {
        startRow: 1,
        endRow: 1,
        startCol: contentStartCol,
        width: contentWidth,
        height: 1,
      };

      const contentEndRow = footer ? rows - 2 : rows;
      const content: PanelRegion = {
        startRow: 3,
        endRow: contentEndRow,
        startCol: contentStartCol,
        width: contentWidth,
        height: contentEndRow - 2,
      };

      const footerRegion: PanelRegion = {
        startRow: footerRow,
        endRow: footerRow,
        startCol: contentStartCol,
        width: contentWidth,
        height: 1,
      };

      const layout: FrameLayout = {
        cols,
        rows,
        header,
        content,
        footer: footerRegion,
      };

      // Two-column support (no outer border chars to worry about)
      if (twoColumn && contentWidth > 40) {
        const leftWidth = Math.floor(cols * leftRatio);
        const dividerCol = leftWidth;

        // Draw vertical divider
        for (let r = 3; r <= contentEndRow; r++) {
          this.terminal.setCursor(r, dividerCol);
          this.terminal.writeRaw(`${bc}║${RESET}`);
        }
        // Draw T-junctions on separator rows (use corner color for visibility)
        this.terminal.setCursor(2, dividerCol);
        this.terminal.writeRaw(`${cc}╦${RESET}`);
        if (footer) {
          this.terminal.setCursor(rows - 1, dividerCol);
          this.terminal.writeRaw(`${cc}╩${RESET}`);
        }

        layout.leftPanel = {
          startRow: 3,
          endRow: contentEndRow,
          startCol: 1 + contentPadding,
          width: leftWidth - contentPadding - 1,
          height: contentEndRow - 2,
        };

        layout.rightPanel = {
          startRow: 3,
          endRow: contentEndRow,
          startCol: dividerCol + 2,
          width: cols - dividerCol - 2,
          height: contentEndRow - 2,
        };
      }

      this.currentLayout = layout;
      return layout;

    } else {
      // ── Original ASCII border mode ──

      // Row 1: Top border with title
      this.terminal.setCursor(1, 1);
      const titleText = title ? ` ${title} ` : '';
      let rightText = headerRight ? ` ${headerRight} ` : '';
      // Available space for right text: total cols minus 4 border chars (╔═ + ═╗), title, padding, and 4 minimum fill ═
      const availableForRight = cols - 4 - titleText.length - this.HEADER_RIGHT_PADDING - 4;
      if (rightText.length > 0) {
        if (availableForRight < 6) {
          rightText = ''; // Not enough space for meaningful right text
        } else if (rightText.length > availableForRight) {
          rightText = ` ${headerRight.substring(0, Math.max(1, availableForRight - 5))}… `;
        }
      }
      // Account for all 4 border chars (╔═ left + ═╗ right) plus padding in fill calculation
      const titleFill = cols - 4 - titleText.length - rightText.length - (rightText.length > 0 ? this.HEADER_RIGHT_PADDING : 0);
      this.terminal.writeRaw(
        `${cc}╔${bc}═${hc}${titleText}${bc}${'═'.repeat(Math.max(0, titleFill))}${hc}${rightText}${bc}${'═'.repeat(rightText.length > 0 ? this.HEADER_RIGHT_PADDING : 0)}═${cc}╗${RESET}`
      );

      // Row 2: Header separator
      this.terminal.setCursor(2, 1);
      this.terminal.writeRaw(`${cc}╠${bc}${'═'.repeat(Math.max(0, cols - 2))}${cc}╣${RESET}`);

      // Content area with side borders
      const contentLastRow = footer ? rows - 2 : rows - 1;
      for (let r = 3; r <= contentLastRow; r++) {
        this.terminal.setCursor(r, 1);
        this.terminal.writeRaw(`${bc}║${RESET}`);
        this.terminal.setCursor(r, cols);
        this.terminal.writeRaw(`${bc}║${RESET}`);
      }

      // Footer separator + bottom border
      const footerRow = footer ? rows - 1 : rows;
      if (footer) {
        this.terminal.setCursor(footerRow, 1);
        const footerPadded = this.padCenter(footer, Math.max(0, cols - 2));
        this.terminal.writeRaw(`${cc}╠${RESET}${theme.fg('khaki')}${footerPadded}${cc}╣${RESET}`);

        this.terminal.setCursor(rows, 1);
        this.terminal.writeRaw(`${cc}╚${bc}${'═'.repeat(Math.max(0, cols - 2))}${cc}╝${RESET}`);
      } else {
        this.terminal.setCursor(rows, 1);
        this.terminal.writeRaw(`${cc}╚${bc}${'═'.repeat(Math.max(0, cols - 2))}${cc}╝${RESET}`);
      }

      // Calculate content regions
      const contentPadding = 2;
      const contentStartCol = 1 + contentPadding;
      const contentWidth = cols - 2 - (contentPadding * 2);

      const header: PanelRegion = {
        startRow: 1,
        endRow: 1,
        startCol: contentStartCol,
        width: contentWidth,
        height: 1,
      };

      const contentEndRow = footer ? rows - 2 : rows - 1;
      const content: PanelRegion = {
        startRow: 3,
        endRow: contentEndRow,
        startCol: contentStartCol,
        width: contentWidth,
        height: contentEndRow - 2,
      };

      const footerRegion: PanelRegion = {
        startRow: footerRow,
        endRow: footerRow,
        startCol: contentStartCol,
        width: contentWidth,
        height: 1,
      };

      const layout: FrameLayout = {
        cols,
        rows,
        header,
        content,
        footer: footerRegion,
      };

      // Two-column support
      if (twoColumn && contentWidth > 40) {
        const leftWidth = Math.floor((cols - 2) * leftRatio);
        const dividerCol = 1 + leftWidth;

        for (let r = 3; r <= contentEndRow; r++) {
          this.terminal.setCursor(r, dividerCol);
          this.terminal.writeRaw(`${bc}║${RESET}`);
        }
        this.terminal.setCursor(2, dividerCol);
        this.terminal.writeRaw(`${bc}╦${RESET}`);
        if (footer) {
          this.terminal.setCursor(rows - 1, dividerCol);
          this.terminal.writeRaw(`${bc}╩${RESET}`);
        } else {
          this.terminal.setCursor(rows, dividerCol);
          this.terminal.writeRaw(`${bc}╩${RESET}`);
        }

        layout.leftPanel = {
          startRow: 3,
          endRow: contentEndRow,
          startCol: 1 + contentPadding,
          width: leftWidth - contentPadding - 1,
          height: contentEndRow - 2,
        };

        layout.rightPanel = {
          startRow: 3,
          endRow: contentEndRow,
          startCol: dividerCol + contentPadding,
          width: cols - dividerCol - contentPadding - 1,
          height: contentEndRow - 2,
        };
      }

      this.currentLayout = layout;
      return layout;
    }
  }

  /**
   * Write text centered within a panel region at a specific row offset.
   */
  writeCentered(panel: PanelRegion, rowOffset: number, text: string, color: ColorName = 'white'): void {
    const row = panel.startRow + rowOffset;
    if (row > panel.endRow) return;

    const stripped = this.stripAnsi(text);
    const padding = Math.max(0, Math.floor((panel.width - stripped.length) / 2));
    this.terminal.setCursor(row, panel.startCol + padding);
    this.terminal.write(text, color);
  }

  /**
   * Write text left-aligned within a panel region at a specific row offset.
   */
  writeInPanel(panel: PanelRegion, rowOffset: number, text: string, color: ColorName = 'white'): void {
    const row = panel.startRow + rowOffset;
    if (row > panel.endRow) return;

    this.terminal.setCursor(row, panel.startCol);
    const stripped = this.stripAnsi(text);
    if (stripped.length > panel.width) {
      this.terminal.write(text.substring(0, panel.width), color);
    } else {
      this.terminal.write(text, color);
    }
  }

  /**
   * Write pre-formatted raw ANSI text left-aligned within a panel.
   */
  writeRawInPanel(panel: PanelRegion, rowOffset: number, rawText: string): void {
    const row = panel.startRow + rowOffset;
    if (row > panel.endRow) return;

    this.terminal.setCursor(row, panel.startCol);
    this.terminal.writeRaw(rawText);
  }

  /**
   * Draw a horizontal divider within the content area.
   */
  drawDivider(row: number, char: string = '─', color: ColorName = 'frostBlue'): void {
    if (!this.currentLayout) return;
    const { cols } = this.currentLayout;
    const bc = theme.fg(color);

    if (this.isCssFrameActive()) {
      this.terminal.setCursor(row, 1);
      this.terminal.writeRaw(`${bc}${char.repeat(cols)}${RESET}`);
    } else {
      this.terminal.setCursor(row, 1);
      this.terminal.writeRaw(`${theme.fg('frostBlue')}╠${bc}${char.repeat(cols - 2)}${theme.fg('frostBlue')}╣${RESET}`);
    }
  }

  /**
   * Draw a decorated section header: ═══ TITLE ═══
   */
  drawSectionHeader(
    panel: PanelRegion,
    rowOffset: number,
    title: string,
    style: 'primary' | 'secondary' = 'primary',
    wingChar = '═',
  ): void {
    const row = panel.startRow + rowOffset;
    const color: ColorName = style === 'primary' ? 'icyWhite' : 'frostSilver';
    const wingColor: ColorName = style === 'primary' ? 'frostBlue' : 'frostSilver';
    const titleText = ` ${title} `;
    const totalWing = Math.max(0, panel.width - titleText.length);
    const leftWing = Math.floor(totalWing / 2);
    const rightWing = totalWing - leftWing;

    this.terminal.setCursor(row, panel.startCol);
    this.terminal.writeRaw(
      `${theme.fg(wingColor)}${wingChar.repeat(leftWing)}${style === 'primary' ? `\x1b[1m` : ''}${theme.fg(color)}${titleText}${RESET}${theme.fg(wingColor)}${wingChar.repeat(rightWing)}${RESET}`
    );
  }

  /**
   * Draw a decorated divider with a pattern.
   */
  drawDecoratedDivider(
    panel: PanelRegion,
    rowOffset: number,
    pattern: 'dots' | 'double' | 'mixed' = 'dots',
    color: ColorName = 'frostBlue',
  ): void {
    const row = panel.startRow + rowOffset;
    let unit: string;
    switch (pattern) {
      case 'dots': unit = '─·'; break;
      case 'double': unit = '─═'; break;
      case 'mixed': unit = '─·─═'; break;
    }
    let line = '';
    while (line.length < panel.width) {
      line += unit;
    }
    line = line.slice(0, panel.width);

    this.terminal.setCursor(row, panel.startCol);
    this.terminal.writeRaw(`${theme.fg(color)}${line}${RESET}`);
  }


  /**
   * Draw a sub-box within a panel for "cards" or info boxes.
   * Returns a PanelRegion for the interior of the sub-box.
   */
  drawInnerBox(
    panel: PanelRegion,
    rowOffset: number,
    height: number,
    title?: string,
    color: ColorName = 'frostBlue',
  ): PanelRegion {
    const startRow = panel.startRow + rowOffset;
    const boxWidth = panel.width;
    const bc = theme.fg(color);

    // Top border
    if (title) {
      const titleText = ` ${title} `;
      const fill = boxWidth - 2 - titleText.length;
      this.terminal.setCursor(startRow, panel.startCol);
      this.terminal.writeRaw(`${bc}┌─${theme.fg('icyWhite')}${titleText}${bc}${'─'.repeat(Math.max(0, fill))}┐${RESET}`);
    } else {
      this.terminal.setCursor(startRow, panel.startCol);
      this.terminal.writeRaw(`${bc}┌${'─'.repeat(boxWidth - 2)}┐${RESET}`);
    }

    // Side borders
    for (let r = 1; r < height - 1; r++) {
      this.terminal.setCursor(startRow + r, panel.startCol);
      this.terminal.writeRaw(`${bc}│${RESET}`);
      this.terminal.setCursor(startRow + r, panel.startCol + boxWidth - 1);
      this.terminal.writeRaw(`${bc}│${RESET}`);
    }

    // Bottom border
    this.terminal.setCursor(startRow + height - 1, panel.startCol);
    this.terminal.writeRaw(`${bc}└${'─'.repeat(boxWidth - 2)}┘${RESET}`);

    return {
      startRow: startRow + 1,
      endRow: startRow + height - 2,
      startCol: panel.startCol + 2,
      width: boxWidth - 4,
      height: height - 2,
    };
  }

  /**
   * Draw a stat box with title and key-value entries using box-drawing characters.
   */
  drawStatBox(
    panel: PanelRegion,
    rowOffset: number,
    title: string,
    entries: Array<{ label: string; value: string; color?: ColorName }>,
    boxColor: ColorName = 'frostBlue',
  ): number {
    const row = panel.startRow + rowOffset;
    const boxWidth = Math.min(panel.width, Math.max(24, title.length + 6));
    const bc = theme.fg(boxColor);
    const innerWidth = boxWidth - 4;

    // Top border with title
    const titleText = ` ${title} `;
    const fill = Math.max(0, boxWidth - 2 - titleText.length);
    this.terminal.setCursor(row, panel.startCol);
    this.terminal.writeRaw(`${bc}┌─${theme.fg('icyWhite')}${titleText}${bc}${'─'.repeat(fill)}┐${RESET}`);

    // Entries
    for (let i = 0; i < entries.length; i++) {
      const e = entries[i];
      const valueColor = e.color ?? 'frostSilver';
      const labelStr = e.label;
      const valueStr = e.value;
      const gap = Math.max(1, innerWidth - labelStr.length - valueStr.length);
      this.terminal.setCursor(row + 1 + i, panel.startCol);
      this.terminal.writeRaw(
        `${bc}│ ${theme.fg('khaki')}${labelStr}${' '.repeat(gap)}${theme.fg(valueColor)}${valueStr}${bc} │${RESET}`
      );
    }

    // Bottom border
    this.terminal.setCursor(row + 1 + entries.length, panel.startCol);
    this.terminal.writeRaw(`${bc}└${'─'.repeat(boxWidth - 2)}┘${RESET}`);

    return 2 + entries.length;
  }

  /**
   * Compact reward box for narrative screens.
   */
  drawRewardBox(
    panel: PanelRegion,
    rowOffset: number,
    rewards: Array<{ text: string; color?: ColorName }>,
    title: string = 'Rewards',
    boxColor: ColorName = 'runeGold',
  ): number {
    if (rewards.length === 0) return 0;
    const row = panel.startRow + rowOffset;
    const maxLen = Math.max(title.length + 4, ...rewards.map(r => this.stripAnsi(r.text).length + 4));
    const boxWidth = Math.min(panel.width, Math.max(20, maxLen + 4));
    const bc = theme.fg(boxColor);

    // Top border with title
    const titleText = ` ${title} `;
    const fill = Math.max(0, boxWidth - 2 - titleText.length);
    this.terminal.setCursor(row, panel.startCol + 2);
    this.terminal.writeRaw(`${bc}┌─${theme.fg('icyWhite')}${titleText}${bc}${'─'.repeat(fill)}┐${RESET}`);

    // Reward entries
    for (let i = 0; i < rewards.length; i++) {
      const r = rewards[i];
      const valueColor = r.color ?? 'runeGold';
      const padded = r.text + ' '.repeat(Math.max(0, boxWidth - 4 - this.stripAnsi(r.text).length));
      this.terminal.setCursor(row + 1 + i, panel.startCol + 2);
      this.terminal.writeRaw(`${bc}│ ${theme.fg(valueColor)}${padded}${bc} │${RESET}`);
    }

    // Bottom border
    this.terminal.setCursor(row + 1 + rewards.length, panel.startCol + 2);
    this.terminal.writeRaw(`${bc}└${'─'.repeat(boxWidth - 2)}┘${RESET}`);

    return 2 + rewards.length;
  }


  clearPanel(panel: PanelRegion): void {
    for (let r = panel.startRow; r <= panel.endRow; r++) {
      this.terminal.setCursor(r, panel.startCol);
      this.terminal.write(' '.repeat(panel.width));
    }
  }

  /**
   * Update the footer text without redrawing the whole frame.
   */
  updateFooter(text: string): void {
    if (!this.currentLayout) return;
    const { cols, rows } = this.currentLayout;
    const bc = theme.fg('frostBlue');

    if (this.isCssFrameActive()) {
      if (this.terminalFrame) {
        this.terminalFrame.updateBottomBar(text);
      }
      this.terminal.setCursor(rows - 1, 1);
      const footerPadded = this.padCenter(text, cols);
      this.terminal.writeRaw(`${bc}${'─'.repeat(cols)}${RESET}`);
      this.terminal.setCursor(rows - 1, 1);
      this.terminal.writeRaw(`${theme.fg('frostSilver')}${footerPadded}${RESET}`);
    } else {
      this.terminal.setCursor(rows - 1, 1);
      const footerPadded = this.padCenter(text, cols - 2);
      this.terminal.writeRaw(`${bc}╠${RESET}${theme.fg('frostSilver')}${footerPadded}${bc}╣${RESET}`);
    }
  }

  /**
   * Clear the footer text (both terminal and CSS bottom bar).
   */
  clearFooter(): void {
    if (this.terminalFrame) {
      this.terminalFrame.updateBottomBar('');
    }
  }

  // ─── Helpers ─────────────────────────────────────

  private padCenter(text: string, width: number): string {
    const stripped = this.stripAnsi(text);
    if (stripped.length >= width) return text;
    const leftPad = Math.floor((width - stripped.length) / 2);
    const rightPad = width - stripped.length - leftPad;
    return ' '.repeat(leftPad) + text + ' '.repeat(rightPad);
  }

  private stripAnsi(text: string): string {
    // eslint-disable-next-line no-control-regex
    return text.replace(/\x1b\[[0-9;]*m/g, '');
  }
}
